<template>
  <component :is="tag" part="base" class="sp-text" :class="classModifiers" :href="href">
    <slot />
  </component>
</template>

<script>
const defaultType = "body";
const types = [
  defaultType,
  "display",
  "h1",
  "h2",
  "h3",
  "h4",
  "title",
  "subtitle",
  "body",
  "body-semi-bold",
  "body-bold",
  "link",
  "small",
  "small-bold",
  "footnote",
  "footnote-medium",
  "footnote-bold",
  "label",
  "custom",
];
</script>

<script setup>
import { props as textProps } from "./props.js";
import { toBoolean } from "#ce/utils/props";
import { computed } from "vue";
import { useBreakpoints } from "#ce/composables/useBreakpoints";

const props = defineProps(textProps);

const type = computed(() => {
  if (props.type) {
    return props.type;
  }
  return types.includes(props.tag) ? props.tag : defaultType;
});

const { breakpointModifiers } = useBreakpoints();

const classModifiers = computed(() => {
  const modifiers = { [`--${type.value}`]: type.value };

  if (toBoolean(props.noSmallScreenType)) {
    modifiers["--no-small-screen-type"] = true;
  }

  return [...breakpointModifiers.value, modifiers];
});
</script>

<style>
:host {
  display: block;
  margin: 0;
  box-sizing: border-box;
  --text-color: var(--sp-comp-text-color, var(--sp-sys-color-on-surface, #000));
}
:host([color]) {
  --text-color: v-bind(color);
}
:host([type="custom"]) {
  --font: var(--sp-comp-text-custom-font-short, var(--sp-sys-typescale-body-short));
}
</style>

<style lang="scss" scoped>
%reset {
  margin: 0;
  padding: 0;
}

.sp-text,
.--body {
  color: var(--text-color);
  font: var(--sp-sys-typescale-body-short);
  @extend %reset;
}

$types: (
  "display",
  h1,
  h2,
  h3,
  h4,
  "body",
  "body-semi-bold",
  "body-bold",
  "label",
  "link",
  "small",
  "small-bold",
  "footnote",
  "footnote-medium",
  "footnote-bold"
);

@each $type in $types {
  .--#{$type} {
    --font: var(--sp-sys-typescale-#{$type}-short);

    &.--is-sm-screen:not(.--no-small-screen-type) {
      --font: var(--sp-sys-typescale-#{$type}-small-short, var(--sp-sys-typescale-#{$type}-short));
    }

    font: var(--font);
  }
}

.--title,
.--subtitle,
.--label,
.--custom {
  @extend %reset;
}

.--title {
  font: var(--sp-comp-text-title-short, var(--sp-sys-typescale-h4-short));
}

.--subtitle {
  font: var(--sp-comp-text-subtitle-short, var(--sp-sys-typescale-body-semi-bold-short));
}

.--label {
  font: var(--sp-comp-text-label-short, var(--sp-sys-form-field-label-short));
}

.--custom {
  font: var(--font);
}

.--link {
  text-decoration: var(--sp-comp-text-link-text-decoration, var(--sp-sys-typescale-link-text-decoration, underline));
}

.sp-text.--inherit {
  color: inherit;
  font: inherit;
}

:host([color]) .sp-text.--inherit {
  color: var(--text-color);
}
</style>
